import {CASE_SPIN_MS,caseTickTimes} from "./case-reel.js";
import { registerInstances } from "./assets.js";
import { supportedWear } from "./crate-preview.js";
import { cosmeticSlot } from "../shared/loadout.js";
import { request, toast, escape as e } from "./api.js";
import { itemDetails, crateDetails } from "./inventory.js";
import type { View } from "./pages.js";
import type { Game } from "./game.js";
export function bindInventory(
  root: HTMLElement,
  get: () => View,
  game: Game,
  render: () => Promise<void>,
  refresh: () => Promise<void>,
) {
  let busy = false,
    revealTimer: ReturnType<typeof setTimeout>,
    searchTimer: ReturnType<typeof setTimeout>;
  let tickTimers: ReturnType<typeof setTimeout>[] = [];
  const clearReveal = () => { clearTimeout(revealTimer); tickTimers.forEach(clearTimeout); tickTimers = []; };
  const armReveal = () => {
    clearReveal();
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduced) tickTimers = caseTickTimes().map(ms => setTimeout(() => { if(get().revealing && root.querySelector(".case-strip")) game.audio.play("hover"); }, ms));
    revealTimer = setTimeout(() => void finishReveal(), reduced ? 250 : CASE_SPIN_MS);
  };
  let lastHover = 0,
    drag: { x: number; angle: number; id: number; el: HTMLElement } | undefined;
  const closeMenu = () => root.querySelector(".inventory-context")?.remove();
  const item = (id: string) =>
    get().content.cosmetics.find((c: any) => c.id === id);
  const preview = async () => {
    const el = root.querySelector<HTMLElement>("[data-preview-id]");
    if (el) await game.renderer.showPreview(el.dataset.previewId!);
  };
  const select = async (id: string, tier?: number) => {
    const v = get();
    if (tier) v.crateTier = tier;
    else v.preview = id;
    root.querySelectorAll<HTMLElement>(".item-tile").forEach((el) => {
      const selected = tier
        ? Number(el.dataset.inventoryTier) === tier
        : el.dataset.inventoryId === id;
      el.classList.toggle("selected", selected);
      el.setAttribute("aria-selected", String(selected));
    });
    const panel = root.querySelector(".inventory-presentation");
    if (panel)
      panel.outerHTML = tier ? crateDetails(v, tier) : itemDetails(v, item(id));
    await preview();
  };
  const equip = async (id: string) => {
    const c = item(id);
    if (!c || !get().user.cosmetics?.includes(id)) return;
    await request("/api/equip", { id });
    await refresh();
    await redraw();
    game.audio.play("equip");
  };
  const redraw = async () => {
    const grid = root.querySelector(".item-grid"),
      scroll = grid?.scrollTop ?? 0;
    await render();
    const next = root.querySelector(".item-grid");
    if (next) next.scrollTop = scroll;
  };
  const finishReveal = async () => {
    if (!get().revealing) return;
    clearReveal();
    get().revealing = false;
    await redraw();
    game.audio.play("reveal");
  };
  const open = async (tier: number, all: boolean) => {
    if (busy) return;
    const v = get(),
      ids = ((v.user as any).crates ?? [])
        .filter((c: any) => c.tier === tier)
        .slice(0, all ? 100 : 1)
        .map((c: any) => c.id);
    if (!ids.length) return;
    busy = true;
    try {
      root
        .querySelectorAll<HTMLButtonElement>(
          '[data-action="open-crate"],[data-action="open-all"]',
        )
        .forEach((el) => (el.disabled = true));
      const result = await request("/api/crates/open", { ids });
      await refresh();
      Object.assign(get(), {
        crateResults: result.results,
        revealIndex: 0,
        revealing: true,
      });
      game.audio.play("case-open");
      await redraw();
      armReveal();
    } catch (error) {
      await redraw();
      throw error;
    } finally {
      busy = false;
    }
  };
  let previewGeneration = 0;
  const contentPreview = async (id: string, seed = 387, wear?: number) => {
    const generation = ++previewGeneration,
      v = get(),
      c = item(id);
    const grade = wear ?? supportedWear(c)[0] ?? 0;
    const preview = await request(
      `/api/crates/${v.crateTier ?? 1}/preview?id=${encodeURIComponent(id)}&seed=${seed}&wear=${grade}`,
    );
    if (generation !== previewGeneration || !get().crateContents) return;
    v.cratePreview = { ...preview, wearChoice: grade };
    await registerInstances([preview]);
    await redraw();
  };
  const actions = new Set([
    "crate-contents",
    "close-crate-contents",
    "preview-content",
    "inventory-category",
    "inventory-side",
    "preview-item",
    "preview-crate",
    "inspect-item",
    "inspect-animation",
    "reset-preview",
    "close-inspect",
    "open-crate",
    "open-all",
    "skip-reveal",
    "close-reveal",
    "next-reveal",
  ]);
  root.addEventListener(
    "click",
    (event) => {
      const el = (event.target as Element).closest<HTMLElement>(
        "[data-action]",
      );
      const action = el?.dataset.action;
      if (!el || !action) {
        closeMenu();
        return;
      }
      const isEquip =
        action === "equip" && !!root.querySelector(".inventory-v4");
      if (!actions.has(action) && !isEquip) {
        closeMenu();
        return;
      }
      event.stopImmediatePropagation();
      closeMenu();
      void (async () => {
        const v = get(),
          id = el.dataset.id!;
        if (action === "crate-contents") {
          v.crateTier = Number(el.dataset.tier);
          v.crateContents = true;
          v.cratePreview = undefined;
          v.crateSearch = "";
          await redraw();
        }
        if (action === "close-crate-contents") {
          ++previewGeneration;
          v.crateContents = false;
          v.cratePreview = undefined;
          await redraw();
        }
        if (action === "preview-content") await contentPreview(id);
        if (action === "inventory-category" || action === "inventory-side") {
          if (action === "inventory-category")
            v.inventoryCategory = el.dataset.category;
          else v.inventorySide = el.dataset.side;
          v.crateContents = false;
          v.preview = undefined;
          v.inspect = false;
          await render();
          game.audio.play("select");
        }
        if (action === "preview-item") {
          await select(id);
          game.audio.play("select");
        }
        if (action === "preview-crate") {
          await select("", Number(el.dataset.tier));
          game.audio.play("select");
        }
        if (action === "equip") await equip(id);
        if (action === "inspect-item" && !v.crateContents) {
          v.preview = id || v.preview;
          v.inspect = true;
          await redraw();
        }
        if (action === "close-inspect") {
          v.inspect = false;
          await redraw();
        }
        if (action === "reset-preview") {
          game.renderer.resetPreview();
        }
        if (action === "inspect-animation") {
          game.renderer.inspectPreview();
          game.audio.play("inspect");
        }
        if (action === "open-crate" || action === "open-all")
          await open(Number(el.dataset.tier), action === "open-all");
        if (action === "skip-reveal") await finishReveal();
        if (action === "close-reveal") {
          clearReveal();
          v.crateResults = undefined;
          v.revealing = false;
          await redraw();
        }
        if (action === "next-reveal") {
          v.revealIndex = (v.revealIndex ?? 0) + 1;
          v.revealing = true;
          game.audio.play("case-open");
          await redraw();
          armReveal();
        }
      })().catch((err) => toast(err.message));
    },
    true,
  );
  root.addEventListener("change", (event) => {
    const el = event.target as HTMLSelectElement,
      v = get();
    if (["preview-seed", "preview-wear"].includes(el.id) && v.cratePreview)
      void contentPreview(
        v.cratePreview.templateId,
        Number(
          root.querySelector<HTMLSelectElement>("#preview-seed")?.value ?? 387,
        ),
        Number(
          root.querySelector<HTMLSelectElement>("#preview-wear")?.value ?? 0,
        ),
      ).catch((err) => toast(err.message));
  });
  root.addEventListener("input", (event) => {
    const el = event.target as HTMLInputElement;
    if (el.id !== "crate-search") return;
    get().crateSearch = el.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(
      () =>
        void redraw().then(() => {
          const input = root.querySelector<HTMLInputElement>("#crate-search");
          input?.focus();
          input?.setSelectionRange(input.value.length, input.value.length);
        }),
      180,
    );
  });
  root.addEventListener("dblclick", (event) => {
    const el = (event.target as Element).closest<HTMLElement>(
      "[data-inventory-id],[data-inventory-tier]",
    );
    if (!el) return;
    event.preventDefault();
    event.stopPropagation();
    closeMenu();
    void (
      el.dataset.inventoryTier
        ? open(Number(el.dataset.inventoryTier), false)
        : equip(el.dataset.inventoryId!)
    ).catch((err) => toast(err.message));
  });
  root.addEventListener("contextmenu", (event) => {
    const el = (event.target as Element).closest<HTMLElement>(
      "[data-inventory-id],[data-inventory-tier]",
    );
    if (!el) return;
    event.preventDefault();
    closeMenu();
    const v = get(),
      id = el.dataset.inventoryId,
      tier = Number(el.dataset.inventoryTier);
    const c = id ? item(id) : null,
      isOwned = c && v.user.cosmetics?.includes(id!);
    const qty = ((v.user as any).crates ?? []).filter(
      (c: any) => c.tier === tier,
    ).length;
    const button = (name: string, action: string, extra = "") =>
      `<button role="menuitem" data-action="${action}" ${id ? `data-id="${e(id)}"` : `data-tier="${tier}"`} ${extra}>${name}</button>`;
    const menu = document.createElement("div");
    menu.className = "inventory-context";
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", "Item actions");
    menu.innerHTML = tier
      ? button("Open", "open-crate") +
        (qty > 1 ? button(`Open all (${qty})`, "open-all") : "")
      : button("Inspect", "inspect-item") +
        (isOwned
          ? v.user.equipped[cosmeticSlot(c)] !== id
            ? button("Equip", "equip")
            : ""
          : button(
              "View unlock source",
              "preview-item",
              'class="context-source"',
            ));
    menu.style.left = Math.min(event.clientX, innerWidth - 225) + "px";
    menu.style.top = Math.min(event.clientY, innerHeight - 160) + "px";
    root.append(menu);
    menu.querySelector("button")?.focus();
    game.audio.play("context");
  });
  root.addEventListener("pointerdown", (event) => {
    const el = (event.target as Element).closest<HTMLElement>(".item-stage");
    if (!el || event.button !== 0) return;
    drag = {
      x: event.clientX,
      angle: game.renderer.previewRotation,
      id: event.pointerId,
      el,
    };
    el.setPointerCapture(event.pointerId);
  });
  root.addEventListener("pointermove", (event) => {
    if (drag)
      game.renderer.previewRotation =
        drag.angle + (event.clientX - drag.x) * 0.009;
  });
  const endDrag = () => {
    if (drag?.el.hasPointerCapture(drag.id))
      drag.el.releasePointerCapture(drag.id);
    drag = undefined;
  };
  root.addEventListener("pointerup", endDrag);
  root.addEventListener("pointercancel", endDrag);
  root.addEventListener(
    "wheel",
    (event) => {
      if ((event.target as Element).closest(".item-stage")) {
        event.preventDefault();
        game.renderer.previewZoom = Math.max(
          0.5,
          Math.min(1.8, game.renderer.previewZoom + event.deltaY * 0.0007),
        );
      }
    },
    { passive: false },
  );
  root.addEventListener("pointerover", (event) => {
    if (
      (event.target as Element).closest(".item-tile") &&
      performance.now() - lastHover > 180
    ) {
      lastHover = performance.now();
      game.audio.play("hover");
    }
  });
  root.addEventListener("change", (event) => {
    const el = event.target as HTMLSelectElement;
    if (el.id !== "inventory-filter" && el.id !== "inventory-sort") return;
    if (el.id === "inventory-filter") get().inventoryFilter = el.value;
    else get().inventorySort = el.value;
    void render();
  });
  root.addEventListener("input", (event) => {
    const el = event.target as HTMLInputElement;
    if (el.id !== "inventory-search") return;
    get().inventorySearch = el.value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(
      () =>
        void (async () => {
          const cursor = el.selectionStart;
          await render();
          const input =
            root.querySelector<HTMLInputElement>("#inventory-search");
          input?.focus();
          if (input?.type === "text") input.setSelectionRange(cursor, cursor);
        })(),
      120,
    );
  });
  root.addEventListener("scroll", closeMenu, true);
  document.addEventListener("keydown", (event) => {
    if (event.code === "Escape") {
      closeMenu();
      const v = get();
      if (v.crateResults) {
        clearReveal();
        v.crateResults = undefined;
        v.revealing = false;
        void redraw();
      } else if (v.inspect) {
        v.inspect = false;
        void redraw();
      }
    }
    const menu = root.querySelector(".inventory-context");
    if (!menu) return;
    if (event.code === "ArrowDown" || event.code === "ArrowUp") {
      event.preventDefault();
      const els = [...menu.querySelectorAll("button")],
        i = els.indexOf(document.activeElement as HTMLButtonElement);
      els[
        (i + (event.code === "ArrowDown" ? 1 : els.length - 1)) % els.length
      ]?.focus();
    }
  });
  window.addEventListener("resize", closeMenu);
}
